/**
 * useWizardV2.ts
 *
 * TanStack Query hooks for the two-bar interactive presentation wizard (Sprint 1).
 *
 * All endpoints are under /api/providers/presentations/.
 * Follows the same pattern as usePresentationWizard.ts:
 *  - KEYS factory object with `as const`
 *  - Named exports only
 *  - Import `api` from `@/lib/axios`
 */

import { useQuery, useMutation } from '@tanstack/react-query';

import { api } from '@/lib/axios';
import type {
  NarrativePlan,
  SlideConfig,
  ImageSlotConfig,
  BaseProduct,
  CostTableConfig,
  WizardDraftData,
  WizardQuestion,
} from '@/types/wizardV2';

// ─── Query key factory ───────────────────────────────────────────────────────

/**
 * Stable query key factory for all wizard-v2 queries.
 * Use these keys when you need to invalidate or prefetch from outside the hooks.
 */
export const WIZARD_V2_KEYS = {
  all: ['wizard-v2'] as const,

  /** Detail key for a single draft. */
  draft: (id: string) => [...WIZARD_V2_KEYS.all, 'draft', id] as const,

  /** Image-prompts list for a presentation. */
  imagePrompts: (id: string) => [...WIZARD_V2_KEYS.all, 'image-prompts', id] as const,

  /** Global base-products catalog (rarely changes — long stale time). */
  baseProducts: () => [...WIZARD_V2_KEYS.all, 'base-products'] as const,

  /** Cost data for a specific presentation. */
  costData: (id: string) => [...WIZARD_V2_KEYS.all, 'cost-data', id] as const,
} as const;

// ─── Shared base path ────────────────────────────────────────────────────────

const BASE = '/api/providers/presentations';

// ─── Draft management ────────────────────────────────────────────────────────

/**
 * Create a new presentation draft.
 * Called as soon as the user selects a city in Setup step 1.
 * POST /api/providers/presentations/wizard-draft/
 */
export const useCreateDraft = () =>
  useMutation<WizardDraftData, Error, Record<string, unknown>>({
    mutationFn: (data) =>
      api.post(`${BASE}/wizard-draft/`, data).then((r) => r.data as WizardDraftData),
  });

/**
 * Fetch a presentation draft by UUID.
 * Used to rehydrate the wizard after a page refresh.
 * GET /api/providers/presentations/{id}/wizard-draft/
 */
export const useFetchDraft = (id: string) =>
  useQuery<WizardDraftData>({
    queryKey: WIZARD_V2_KEYS.draft(id),
    queryFn: () =>
      api.get(`${BASE}/${id}/wizard-draft/`).then((r) => r.data as WizardDraftData),
    enabled: !!id,
    staleTime: 30_000,
  });

/**
 * Save partial changes to a draft (debounced auto-save on each Setup step).
 * PATCH /api/providers/presentations/{id}/wizard-draft/
 */
export const useUpdateDraft = () =>
  useMutation<WizardDraftData, Error, { id: string; data: Record<string, unknown> }>({
    mutationFn: ({ id, data }) =>
      api.patch(`${BASE}/${id}/wizard-draft/`, data).then((r) => r.data as WizardDraftData),
  });

// ─── Build step 1: Preguntas ─────────────────────────────────────────────────

/**
 * Generate AI personalisation questions for the given draft.
 *
 * Returns up to 5 questions that help the AI tailor the presentation to the
 * client's specific needs. Results are stored in the wizard store and
 * displayed by QuestionsStep.
 *
 * POST /api/providers/presentations/{id}/generate-questions/
 *
 * @returns Mutation that resolves to the list of `WizardQuestion` objects.
 */
export const useGenerateQuestions = () =>
  useMutation<WizardQuestion[], Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post(`${BASE}/${id}/generate-questions/`).then((r) => {
        const questions = r.data?.questions ?? [];
        return questions as WizardQuestion[];
      }),
  });

/**
 * Persist the user's answers to the generated questions.
 *
 * Answers are optional context for the AI plan and slide generators. Calling
 * this endpoint is not required to advance to the next step — "Omitir"
 * skips it entirely.
 *
 * POST /api/providers/presentations/{id}/save-question-answers/
 *
 * @returns Mutation that resolves to void on success.
 */
export const useSaveQuestionAnswers = () =>
  useMutation<void, Error, { id: string; answers: Array<{ id: string; answer: string }> }>({
    mutationFn: ({ id, answers }) =>
      api.post(`${BASE}/${id}/save-question-answers/`, { answers }),
  });

// ─── Build step 2: Planificación ─────────────────────────────────────────────

/**
 * Trigger AI narrative-plan generation for the given draft.
 * POST /api/providers/presentations/{id}/generate-plan/
 */
export const useGeneratePlan = () =>
  useMutation<NarrativePlan, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post(`${BASE}/${id}/generate-plan/`).then((r) => r.data as NarrativePlan),
  });

/**
 * Send a free-text message to the plan-assistant chat.
 * The assistant returns an updated NarrativePlan (or plain markdown).
 * POST /api/providers/presentations/{id}/plan-chat/
 */
export const usePlanAssist = () =>
  useMutation<
    { plan: NarrativePlan; markdown: string },
    Error,
    { id: string; instruction: string; currentPlanMd: string }
  >({
    mutationFn: ({ id, instruction, currentPlanMd }) =>
      api
        .post(`${BASE}/${id}/plan-chat/`, {
          instruction,
          current_plan_md: currentPlanMd,
        })
        .then((r) => r.data as { plan: NarrativePlan; markdown: string }),
  });

// ─── Build step 3: Diapositivas ──────────────────────────────────────────────

/**
 * Generate the full slide-config array from the approved narrative plan.
 * POST /api/providers/presentations/{id}/generate-slides/
 */
export const useGenerateSlides = () =>
  useMutation<SlideConfig[], Error, { id: string; plan?: NarrativePlan }>({
    mutationFn: ({ id, plan }) =>
      api
        .post(`${BASE}/${id}/generate-slides/`, plan ? { plan } : undefined)
        .then((r) => (r.data?.slides ?? r.data) as SlideConfig[]),
  });

/**
 * Persist the current slide configuration back to the backend.
 * PATCH /api/providers/presentations/{id}/update-slides/
 */
export const useSaveSlides = () =>
  useMutation<SlideConfig[], Error, { id: string; slides: SlideConfig[] }>({
    mutationFn: ({ id, slides }) =>
      api
        .patch(`${BASE}/${id}/update-slides/`, { slides })
        .then((r) => r.data as SlideConfig[]),
  });

// ─── Build step 4: Imágenes ──────────────────────────────────────────────────

/**
 * Fetch the image-prompt list for a presentation (pre-populated from slides).
 * GET /api/providers/presentations/{id}/image-prompts/
 */
export const useImagePrompts = (id: string) =>
  useQuery<ImageSlotConfig[]>({
    queryKey: WIZARD_V2_KEYS.imagePrompts(id),
    queryFn: () =>
      api
        .get(`${BASE}/${id}/image-prompts/`)
        .then((r) => r.data as ImageSlotConfig[]),
    enabled: !!id,
    staleTime: 60_000,
  });

/**
 * Fetch the global base-products catalog (equipment / merchandise cut-outs).
 * GET /api/providers/base-products/
 */
export const useBaseProducts = () =>
  useQuery<BaseProduct[]>({
    queryKey: WIZARD_V2_KEYS.baseProducts(),
    queryFn: () =>
      api.get('/api/providers/base-products/').then((r) => r.data as BaseProduct[]),
    staleTime: 10 * 60_000, // 10 minutes — catalog rarely changes
  });

/**
 * Send a free-text message to the prompt-rewrite assistant for a single image slot.
 * Returns the improved prompt string.
 * POST /api/providers/presentations/{id}/prompt-chat/
 */
export const usePromptAssist = () =>
  useMutation<
    { prompt: string },
    Error,
    { id: string; instruction: string; currentPrompt: string; slideContext?: Record<string, string> }
  >({
    mutationFn: ({ id, instruction, currentPrompt, slideContext }) =>
      api
        .post(`${BASE}/${id}/prompt-chat/`, {
          instruction,
          current_prompt: currentPrompt,
          slide_context: slideContext,
        })
        .then((r) => r.data as { prompt: string }),
  });

/**
 * Trigger image generation for a single slot.
 * Returns the generated image URL.
 * POST /api/providers/presentations/{id}/generate-image/
 */
export const useGenerateImage = () =>
  useMutation<
    { url: string },
    Error,
    {
      id: string;
      slotKey: string;
      prompt: string;
      provider?: 'gemini' | 'openai';
      referenceImageUrls?: string[];
    }
  >({
    mutationFn: ({ id, slotKey, prompt, provider = 'gemini', referenceImageUrls = [] }) =>
      api
        .post(`${BASE}/${id}/generate-image/`, {
          slot_key: slotKey,
          prompt,
          provider,
          reference_image_urls: referenceImageUrls,
        })
        .then((r) => r.data as { url: string }),
  });

/**
 * Remove the background from an already-generated image.
 * Returns the processed image URL.
 * POST /api/providers/presentations/{id}/remove-background/
 */
export const useRemoveBackground = () =>
  useMutation<{ url: string }, Error, { id: string; slotKey: string; imageUrl: string; method: 'pil' | 'ai' }>({
    mutationFn: ({ id, slotKey, imageUrl, method }) =>
      api
        .post(`${BASE}/${id}/remove-background/`, {
          slot_key: slotKey,
          image_url: imageUrl,
          method,
        })
        .then((r) => r.data as { url: string }),
  });

// ─── Build step 5: Costos ────────────────────────────────────────────────────

/**
 * Fetch cost tables derived from the selected providers and quotation sessions.
 * GET /api/providers/presentations/{id}/cost-data/
 */
export const useCostData = (id: string) =>
  useQuery<CostTableConfig[]>({
    queryKey: WIZARD_V2_KEYS.costData(id),
    queryFn: () =>
      api.get(`${BASE}/${id}/cost-data/`).then((r) => {
        const raw = r.data?.tables ?? r.data ?? [];
        if (!Array.isArray(raw)) return [];
        return raw.map(
          (t: Record<string, unknown>, idx: number): CostTableConfig => ({
            slideIndex: (t.slideIndex as number) ?? (t.slide_index as number) ?? idx,
            title: (t.title as string) ?? `Tabla ${idx + 1}`,
            total: Number(t.total ?? 0),
            rows: Array.isArray(t.rows)
              ? (t.rows as Record<string, unknown>[]).map((r) => ({
                  service: String(r.service ?? ''),
                  provider: String(r.provider ?? ''),
                  basePrice: Number(r.basePrice ?? r.base_price ?? 0),
                  marginPercent: Number(r.marginPercent ?? r.margin_percent ?? 0),
                  clientPrice: Number(r.clientPrice ?? r.client_price ?? 0),
                }))
              : [],
          }),
        );
      }),
    enabled: !!id,
    staleTime: 30_000,
  });

/**
 * Persist updated cost tables (including margin adjustments) to the backend.
 * POST /api/providers/presentations/{id}/update-costs/
 */
export const useSaveCosts = () =>
  useMutation<CostTableConfig[], Error, { id: string; tables: CostTableConfig[] }>({
    mutationFn: ({ id, tables }) => {
      const payload = tables.map((t) => ({
        title: t.title,
        total: t.total,
        rows: t.rows.map((r) => ({
          service: r.service,
          provider: r.provider,
          base_price: r.basePrice,
          margin_percent: r.marginPercent,
          client_price: r.clientPrice,
        })),
      }));
      return api
        .post(`${BASE}/${id}/update-costs/`, { tables: payload })
        .then((r) => (r.data?.tables ?? []) as CostTableConfig[]);
    },
  });

/**
 * Send a free-text message to the cost-adjustment assistant.
 * Returns updated cost tables.
 * POST /api/providers/presentations/{id}/cost-chat/
 */
export const useCostAssist = () =>
  useMutation<
    { tables: CostTableConfig[] },
    Error,
    { id: string; instruction: string; currentTables: CostTableConfig[] }
  >({
    mutationFn: ({ id, instruction, currentTables }) => {
      const payload = currentTables.map((t) => ({
        title: t.title,
        total: t.total,
        rows: t.rows.map((r) => ({
          service: r.service,
          provider: r.provider,
          base_price: r.basePrice,
          margin_percent: r.marginPercent,
          client_price: r.clientPrice,
        })),
      }));
      return api
        .post(`${BASE}/${id}/cost-chat/`, {
          instruction,
          current_tables: payload,
        })
        .then((r) => {
          const raw = r.data?.tables ?? [];
          const tables: CostTableConfig[] = Array.isArray(raw)
            ? raw.map((t: Record<string, unknown>, idx: number) => ({
                slideIndex: Number(t.slideIndex ?? t.slide_index ?? idx),
                title: String(t.title ?? `Table ${idx + 1}`),
                total: Number(t.total ?? 0),
                rows: Array.isArray(t.rows)
                  ? (t.rows as Record<string, unknown>[]).map((row) => ({
                      service: String(row.service ?? ''),
                      provider: String(row.provider ?? ''),
                      basePrice: Number(row.basePrice ?? row.base_price ?? 0),
                      marginPercent: Number(row.marginPercent ?? row.margin_percent ?? 0),
                      clientPrice: Number(row.clientPrice ?? row.client_price ?? 0),
                    }))
                  : [],
              }))
            : [];
          return { tables };
        });
    },
  });

// ─── Build step 6: Generar ───────────────────────────────────────────────────

/**
 * Finalize the draft and trigger the full Celery presentation generation pipeline.
 * After this call, the existing polling infrastructure (generation_progress) takes over.
 * POST /api/providers/presentations/{id}/finalize/
 */
export const useFinalize = () =>
  useMutation<{ task_id: string }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api
        .post(`${BASE}/${id}/finalize/`)
        .then((r) => r.data as { task_id: string }),
  });

/**
 * Finalize a render wizard draft — marks it as completed.
 * The generated_images must already be persisted via PATCH wizard-draft/.
 * POST /api/providers/presentations/{id}/finalize-render/
 */
export const useFinalizeRender = () =>
  useMutation<WizardDraftData, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api
        .post(`${BASE}/${id}/finalize-render/`)
        .then((r) => r.data as WizardDraftData),
  });
